"use client";

import { c as _c } from "react/compiler-runtime";
import { IconLayoutDashboard, IconLogin, IconMenu2, IconMoon, IconSun } from "@tabler/icons-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { AppHeaderSeparator } from "@/components/layout/app-header-separator";
import { UserMenu } from "@/components/layout/user-menu";
import { NotificationsPopover } from "@/components/notifications/notifications-popover";
import { useTheme } from "@/hooks/use-theme";
import { loginHref } from "@/lib/safe-next-path";
import { useSession } from "@domainstack/auth/client";
import { Button } from "@domainstack/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@domainstack/ui/dropdown-menu";
import { useIsClient } from "@domainstack/ui/hooks";
import { Skeleton } from "@domainstack/ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "@domainstack/ui/tooltip";

/**
 * Session-dependent header buttons that conditionally render based on auth state.
 * Client component that uses useSession hook to avoid turning pages into PPR.
 */
export function AppHeaderAccount() {
  const $ = _c(37);
  const {
    data: session,
    isPending
  } = useSession();
  const {
    theme,
    toggleTheme
  } = useTheme();
  const mounted = useIsClient();
  const pathname = usePathname();
  if (!mounted || isPending) {
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <><Skeleton className="size-8 rounded-md max-md:ml-1" /><AppHeaderSeparator className="hidden md:block" /><Skeleton className="hidden h-8 w-[4.75rem] rounded-md md:block" /></>;
      $[0] = t0;
    } else {
      t0 = $[0];
    }
    return t0;
  }
  if (session?.user) {
    let t0;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <><Tooltip><TooltipTrigger render={<Button variant="ghost" size="icon-sm" nativeButton={false} render={<Link href="/dashboard"><IconLayoutDashboard /><span className="sr-only">Dashboard</span></Link>} />} /><TooltipContent>Dashboard</TooltipContent></Tooltip><AppHeaderSeparator /><NotificationsPopover /><AppHeaderSeparator className="mr-2" /><UserMenu /></>;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    return t0;
  }
  let t0;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <DropdownMenuTrigger render={<Button variant="ghost" size="icon-sm" aria-label="Menu" className="md:hidden"><IconMenu2 /></Button>} />;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== theme) {
    t1 = theme === "dark" ? <IconSun /> : <IconMoon />;
    $[3] = theme;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const t2 = theme === "dark" ? "Light mode" : "Dark mode";
  let t3;
  if ($[5] !== t1 || $[6] !== t2 || $[7] !== toggleTheme) {
    t3 = <DropdownMenuItem onClick={toggleTheme} closeOnClick={false}>{t1}{t2}</DropdownMenuItem>;
    $[5] = t1;
    $[6] = t2;
    $[7] = toggleTheme;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <DropdownMenuSeparator />;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== pathname) {
    t5 = loginHref(pathname);
    $[10] = pathname;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <IconLogin />;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t5) {
    t7 = <DropdownMenuItem nativeButton={false} render={<Link href={t5} scroll={false}>{t6}Sign In</Link>} />;
    $[13] = t5;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t3 || $[16] !== t7) {
    t8 = <DropdownMenu>{t0}<DropdownMenuContent align="end" className="min-w-48">{t3}{t4}{t7}</DropdownMenuContent></DropdownMenu>;
    $[15] = t3;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t10;
  let t11;
  let t9;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <IconSun className="scale-100 rotate-0 transition-[transform,opacity] dark:scale-95 dark:-rotate-90 dark:opacity-0" />;
    t10 = <IconMoon className="absolute scale-95 rotate-90 opacity-0 transition-[transform,opacity] dark:scale-100 dark:rotate-0 dark:opacity-100" />;
    t11 = <span className="sr-only">Toggle theme</span>;
    $[18] = t10;
    $[19] = t11;
    $[20] = t9;
  } else {
    t10 = $[18];
    t11 = $[19];
    t9 = $[20];
  }
  let t12;
  if ($[21] !== toggleTheme) {
    t12 = <TooltipTrigger render={<Button aria-label="Toggle theme" variant="ghost" size="sm" onClick={toggleTheme} className="hidden md:inline-flex">{t9}{t10}{t11}</Button>} />;
    $[21] = toggleTheme;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  const t13 = theme === "dark" ? "Dark mode" : "Light mode";
  let t14;
  if ($[23] !== t13) {
    t14 = <TooltipContent>{t13}</TooltipContent>;
    $[23] = t13;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== t12 || $[26] !== t14) {
    t15 = <Tooltip>{t12}{t14}</Tooltip>;
    $[25] = t12;
    $[26] = t14;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <AppHeaderSeparator className="hidden md:block" />;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== pathname) {
    t17 = loginHref(pathname);
    $[29] = pathname;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== t17) {
    t18 = <Button variant="ghost" size="sm" nativeButton={false} aria-label="Sign In" className="hidden md:inline-flex" render={<Link href={t17} scroll={false}>Sign In</Link>} />;
    $[31] = t17;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] !== t15 || $[34] !== t18 || $[35] !== t8) {
    t19 = <>{t8}{t15}{t16}{t18}</>;
    $[33] = t15;
    $[34] = t18;
    $[35] = t8;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  return t19;
}